// =====================================================
// ODONTOGRAM MODAL - Modal de selección de dientes
// =====================================================
import { useState, useEffect, useMemo } from 'react';
import { X, Check, RotateCcw, Grid3X3, Rows3, Square, Info } from 'lucide-react';
import { OdontogramAdult } from './OdontogramAdult';
import { OdontogramChild } from './OdontogramChild';

export type SelectionMode = 'UNIT' | 'QUADRANT' | 'ARCADE';

// Mapeo de tipos de odontograma del backend a modos
export type OdontogramType = 'UNIDAD' | 'CUADRANTE' | 'ARCADA' | null;

const ODONTOGRAM_TYPE_TO_MODE: Record<string, SelectionMode> = {
  'UNIDAD': 'UNIT',
  'CUADRANTE': 'QUADRANT',
  'ARCADA': 'ARCADE',
};

const MODE_LABELS: Record<SelectionMode, string> = {
  'UNIT': 'Unidad (pieza a pieza)',
  'QUADRANT': 'Cuadrante',
  'ARCADE': 'Arcada',
};

export interface OdontogramSelection {
  mode: SelectionMode;
  teeth: string[];
  quadrants: string[];
  arcades: string[];
  displayText: string;
}

interface OdontogramModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selection: OdontogramSelection) => void;
  patientAge: number;
  initialSelection?: OdontogramSelection | null;
  medicalActName?: string;
  /** Tipo de odontograma del acto (restringe el modo de selección) */
  odontogramType?: OdontogramType;
}

// Mapeo de cuadrantes
const QUADRANT_TEETH_ADULT: Record<string, string[]> = {
  Q1: ['18', '17', '16', '15', '14', '13', '12', '11'],
  Q2: ['21', '22', '23', '24', '25', '26', '27', '28'],
  Q3: ['31', '32', '33', '34', '35', '36', '37', '38'],
  Q4: ['41', '42', '43', '44', '45', '46', '47', '48'],
};

const QUADRANT_TEETH_CHILD: Record<string, string[]> = {
  Q1: ['55', '54', '53', '52', '51'],
  Q2: ['61', '62', '63', '64', '65'],
  Q3: ['71', '72', '73', '74', '75'],
  Q4: ['81', '82', '83', '84', '85'],
};

// Mapeo de arcadas
const ARCADE_QUADRANTS: Record<string, string[]> = {
  SUPERIOR: ['Q1', 'Q2'],
  INFERIOR: ['Q3', 'Q4'],
};

export function OdontogramModal({
  isOpen,
  onClose,
  onConfirm,
  patientAge,
  initialSelection,
  medicalActName,
  odontogramType,
}: OdontogramModalProps) {
  const isChild = patientAge < 14;
  const QUADRANT_TEETH = isChild ? QUADRANT_TEETH_CHILD : QUADRANT_TEETH_ADULT;

  // Determinar el modo permitido basado en el tipo de odontograma del acto
  const allowedMode: SelectionMode | null = odontogramType 
    ? ODONTOGRAM_TYPE_TO_MODE[odontogramType] || null 
    : null;
  
  // Si hay un modo restringido, usarlo; si no, usar el inicial o UNIT por defecto
  const defaultMode = allowedMode || initialSelection?.mode || 'UNIT';

  const [mode, setMode] = useState<SelectionMode>(defaultMode);
  const [selectedTeeth, setSelectedTeeth] = useState<Set<string>>(
    new Set(initialSelection?.teeth || [])
  );
  const [selectedQuadrants, setSelectedQuadrants] = useState<Set<string>>(
    new Set(initialSelection?.quadrants || [])
  );
  const [selectedArcades, setSelectedArcades] = useState<Set<string>>(
    new Set(initialSelection?.arcades || [])
  );

  // Resetear selección al cambiar de modo
  useEffect(() => {
    if (!initialSelection) {
      setSelectedTeeth(new Set());
      setSelectedQuadrants(new Set());
      setSelectedArcades(new Set());
    }
  }, [mode]);

  // Calcular todos los dientes seleccionados (combinando todos los modos)
  const allSelectedTeeth = useMemo(() => {
    const teeth = new Set<string>();

    // Dientes individuales
    selectedTeeth.forEach((t) => teeth.add(t));

    // Dientes de cuadrantes
    selectedQuadrants.forEach((q) => {
      QUADRANT_TEETH[q]?.forEach((t) => teeth.add(t));
    });

    // Dientes de arcadas
    selectedArcades.forEach((a) => {
      ARCADE_QUADRANTS[a]?.forEach((q) => {
        QUADRANT_TEETH[q]?.forEach((t) => teeth.add(t));
      });
    });

    return teeth;
  }, [selectedTeeth, selectedQuadrants, selectedArcades, QUADRANT_TEETH]);

  const handleToothClick = (toothNumber: string) => {
    if (mode === 'UNIT') {
      setSelectedTeeth((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(toothNumber)) {
          newSet.delete(toothNumber);
        } else {
          newSet.add(toothNumber);
        }
        return newSet;
      });
    }
  };

  const handleQuadrantClick = (quadrant: string) => {
    if (mode === 'QUADRANT') {
      setSelectedQuadrants((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(quadrant)) {
          newSet.delete(quadrant);
        } else {
          newSet.add(quadrant);
        }
        return newSet;
      });
    }
  };

  const handleArcadeClick = (arcade: string) => {
    if (mode === 'ARCADE') {
      setSelectedArcades((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(arcade)) {
          newSet.delete(arcade);
        } else {
          newSet.add(arcade);
        }
        return newSet;
      });
    }
  };

  const handleReset = () => {
    setSelectedTeeth(new Set());
    setSelectedQuadrants(new Set());
    setSelectedArcades(new Set());
  };

  const generateDisplayText = (): string => {
    const parts: string[] = [];

    if (selectedArcades.size > 0) {
      parts.push(Array.from(selectedArcades).join(', '));
    }

    if (selectedQuadrants.size > 0) {
      parts.push(Array.from(selectedQuadrants).join(', '));
    }

    if (selectedTeeth.size > 0) {
      parts.push(Array.from(selectedTeeth).sort().join(', '));
    }

    return parts.join(' | ') || 'Sin selección';
  };

  const handleConfirm = () => {
    const selection: OdontogramSelection = {
      mode,
      teeth: Array.from(selectedTeeth),
      quadrants: Array.from(selectedQuadrants),
      arcades: Array.from(selectedArcades),
      displayText: generateDisplayText(),
    };
    onConfirm(selection);
  };

  const hasSelection = selectedTeeth.size > 0 || selectedQuadrants.size > 0 || selectedArcades.size > 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />

      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-5xl transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <span className="text-2xl">🦷</span>
                Odontograma {isChild ? 'Infantil' : 'Adulto'}
              </h3>
              {medicalActName && (
                <p className="text-sm text-gray-500 mt-0.5">{medicalActName}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors p-1"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Selector de modo */}
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">Modo de selección:</span>
                
                {/* Si hay modo restringido, mostrar solo ese modo */}
                {allowedMode ? (
                  <div className="flex items-center gap-2">
                    <span className="px-4 py-2 bg-blue-500 text-white text-xs font-bold rounded-lg">
                      {MODE_LABELS[allowedMode]}
                    </span>
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Info className="h-3.5 w-3.5" />
                      <span>Definido por el acto médico</span>
                    </div>
                  </div>
                ) : (
                  /* Si no hay restricción, permitir elegir */
                  <div className="flex bg-white p-1 rounded-lg border border-gray-200">
                    <button
                      onClick={() => setMode('UNIT')}
                      className={`flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-md transition-all ${
                        mode === 'UNIT'
                          ? 'bg-blue-500 text-white shadow-sm'
                          : 'text-gray-500 hover:bg-gray-100'
                      }`}
                    >
                      <Square className="h-3.5 w-3.5" />
                      Unidad
                    </button>
                    <button
                      onClick={() => setMode('QUADRANT')}
                      className={`flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-md transition-all ${
                        mode === 'QUADRANT'
                          ? 'bg-blue-500 text-white shadow-sm'
                          : 'text-gray-500 hover:bg-gray-100'
                      }`}
                    >
                      <Grid3X3 className="h-3.5 w-3.5" />
                      Cuadrante
                    </button>
                    <button
                      onClick={() => setMode('ARCADE')}
                      className={`flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-md transition-all ${
                        mode === 'ARCADE'
                          ? 'bg-blue-500 text-white shadow-sm'
                          : 'text-gray-500 hover:bg-gray-100'
                      }`}
                    >
                      <Rows3 className="h-3.5 w-3.5" />
                      Arcada
                    </button>
                  </div>
                )}
              </div>

              <button
                onClick={handleReset}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Limpiar
              </button>
            </div>

            {/* Selector de cuadrantes (solo visible en modo QUADRANT) */}
            {mode === 'QUADRANT' && (
              <div className="mt-4 flex items-center gap-4">
                <span className="text-sm text-gray-500">Selecciona cuadrantes:</span>
                <div className="flex gap-2">
                  {['Q1', 'Q2', 'Q3', 'Q4'].map((q) => (
                    <button
                      key={q}
                      onClick={() => handleQuadrantClick(q)}
                      className={`w-10 h-10 rounded-lg font-bold text-sm transition-all ${
                        selectedQuadrants.has(q)
                          ? 'bg-blue-500 text-white shadow-md'
                          : 'bg-white border border-gray-300 text-gray-600 hover:border-blue-400'
                      }`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Selector de arcadas (solo visible en modo ARCADE) */}
            {mode === 'ARCADE' && (
              <div className="mt-4 flex items-center gap-4">
                <span className="text-sm text-gray-500">Selecciona arcadas:</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleArcadeClick('SUPERIOR')}
                    className={`px-4 py-2 rounded-lg font-bold text-sm transition-all ${
                      selectedArcades.has('SUPERIOR')
                        ? 'bg-blue-500 text-white shadow-md'
                        : 'bg-white border border-gray-300 text-gray-600 hover:border-blue-400'
                    }`}
                  >
                    Arcada Superior
                  </button>
                  <button
                    onClick={() => handleArcadeClick('INFERIOR')}
                    className={`px-4 py-2 rounded-lg font-bold text-sm transition-all ${
                      selectedArcades.has('INFERIOR')
                        ? 'bg-blue-500 text-white shadow-md'
                        : 'bg-white border border-gray-300 text-gray-600 hover:border-blue-400'
                    }`}
                  >
                    Arcada Inferior
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Odontograma */}
          <div className="p-8 flex justify-center bg-gradient-to-b from-gray-50 to-white">
            {isChild ? (
              <OdontogramChild
                selectedTeeth={allSelectedTeeth}
                onToothClick={handleToothClick}
                selectionMode={mode}
              />
            ) : (
              <OdontogramAdult
                selectedTeeth={allSelectedTeeth}
                onToothClick={handleToothClick}
                selectionMode={mode}
              />
            )}
          </div>

          {/* Resumen de selección */}
          <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm">
                <span className="text-gray-500">Selección actual: </span>
                <span className="font-medium text-gray-900">
                  {hasSelection ? generateDisplayText() : 'Ninguna'}
                </span>
                <span className="text-gray-400 ml-2">
                  ({allSelectedTeeth.size} {allSelectedTeeth.size === 1 ? 'pieza' : 'piezas'})
                </span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 bg-white border-t border-gray-200 rounded-b-2xl">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirm}
              disabled={!hasSelection}
              className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
            >
              <Check className="h-4 w-4" />
              Confirmar selección
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
